import type { IconName } from "@/lib/icons/registry";
import { buildPatientChartHref } from "@/lib/layout/patient-chart-links";
import { PatientEhrQuery, TabType } from "@/lib/layout/types";
import type { Notification } from "@/models/communications/Notification";

/**
 * Presentational + interaction config for a notification type — the row icon, the
 * single call-to-action label, and which patient-chart tab that CTA opens.
 *
 * This is a design-prototype registry mapping a known `type` to how it looks and
 * where its CTA routes. The real per-type behavior (recipients, channels, what an
 * action does server-side) belongs to the future notification type registry.
 */

export interface NotificationDisplay {
  icon: IconName<"regular">;
  // The single CTA rendered on the notification row (e.g. "Review", "Follow Up").
  ctaLabel: string;
  // Patient-chart tab (`?tab=<value>`) the CTA opens for the notification's patient.
  // Absent → the CTA falls back to the notification's own `link`.
  targetTab?: TabType;
}

// Known notification type keys (the `type` string a producer stamps on the resource).
//
// Notifications are SIGNALS — status updates and events that inform the provider. Reading a
// notification is the disposition; nothing accountable is tracked on it. Items that imply
// assigned work (approve a refill, author a prescription, finish a draft note) belong in Tasks,
// not here, and are reached from a signal via the "Create task" promotion action.
export const NotificationType = {
  AppointmentBooked: "appointment_booked",
  AppointmentCancelled: "appointment_cancelled",
  AppointmentRescheduled: "appointment_rescheduled",
  LabsReceived: "labs_received",
  ImagingOrderStatus: "imaging_order_status",
  QuestionnaireCompleted: "questionnaire_completed",
  FaxReceived: "fax_received",
  LabUnpaid: "lab_unpaid", // status: an unpaid lab
  RecordUploaded: "record_uploaded", // patient uploaded records to their chart
  ErxStatus: "erx_status", // status: eRx lifecycle (Submitted / Processing / Cancelled / Filled)
  PriorAuth: "prior_auth", // status: DoseSpot prior authorization
  RxTransmissionIssue: "rx_transmission_issue", // status: DoseSpot transmission failure
  MedsInCart: "meds_in_cart", // meds left in cart, never ordered
  ThreadAssigned: "thread_assigned", // a teammate assigned a patient conversation to the provider
  ThreadRouted: "thread_routed", // a conversation's categories routed it into the provider's inbox
} as const;

/** One of the `NotificationType` values, e.g. "fax_received". */
export type NotificationTypeValue = (typeof NotificationType)[keyof typeof NotificationType];

/**
 * Title of an inbound-fax notification. The live fax producer and the fax backfill both use it, so
 * a backfilled row reads the same as a live one.
 */
export function faxReceivedTitle(senderDisplay: string | undefined): string {
  return senderDisplay ? `New fax received from ${senderDisplay}` : "New fax received";
}

const NOTIFICATION_DISPLAY: Record<string, NotificationDisplay> = {
  [NotificationType.AppointmentBooked]: {
    icon: "calendar-check",
    ctaLabel: "View",
    targetTab: TabType.Appointments,
  },
  [NotificationType.AppointmentCancelled]: {
    icon: "calendar-xmark",
    ctaLabel: "View",
    targetTab: TabType.Appointments,
  },
  [NotificationType.AppointmentRescheduled]: {
    icon: "calendar-day",
    ctaLabel: "View",
    targetTab: TabType.Appointments,
  },
  [NotificationType.LabsReceived]: {
    icon: "flask-vial",
    ctaLabel: "Review",
    targetTab: TabType.Records,
  },
  [NotificationType.ImagingOrderStatus]: {
    icon: "x-ray",
    ctaLabel: "Review",
    targetTab: TabType.Records,
  },
  [NotificationType.QuestionnaireCompleted]: {
    icon: "clipboard-list",
    ctaLabel: "Review",
    targetTab: TabType.Records,
  },
  [NotificationType.FaxReceived]: { icon: "fax", ctaLabel: "Match" }, // opens match-to-patient flow
  [NotificationType.LabUnpaid]: {
    icon: "money-check-alt",
    ctaLabel: "Review",
    targetTab: TabType.Records,
  },
  [NotificationType.RecordUploaded]: {
    icon: "file-circle-plus",
    ctaLabel: "Review",
    targetTab: TabType.Records,
  },
  [NotificationType.ErxStatus]: { icon: "prescription", ctaLabel: "View", targetTab: TabType.Meds },
  [NotificationType.PriorAuth]: {
    icon: "file-prescription",
    ctaLabel: "Review",
    targetTab: TabType.Meds,
  },
  [NotificationType.RxTransmissionIssue]: {
    icon: "triangle-exclamation",
    ctaLabel: "Resolve",
    targetTab: TabType.Meds,
  },
  [NotificationType.MedsInCart]: {
    icon: "circle-exclamation",
    ctaLabel: "Review",
    targetTab: TabType.Meds,
  },
  // conversation types open the thread page (the notification's `link`), not a chart tab
  [NotificationType.ThreadAssigned]: { icon: "comments", ctaLabel: "Open" },
  [NotificationType.ThreadRouted]: { icon: "comments", ctaLabel: "Open" },
};

// Fallback for an unmapped/unknown type — a generic bell with a neutral CTA.
const DEFAULT_NOTIFICATION_DISPLAY: NotificationDisplay = { icon: "bell", ctaLabel: "View" };

/**
 * Resolves the display config for a notification type, falling back to a generic
 * bell for unknown types.
 */
export function getNotificationDisplay(type: string | undefined): NotificationDisplay {
  if (!type) return DEFAULT_NOTIFICATION_DISPLAY;
  return NOTIFICATION_DISPLAY[type] ?? DEFAULT_NOTIFICATION_DISPLAY;
}

//--------------------------------------------------------------------------------------------------
//                                        CATEGORIES
//--------------------------------------------------------------------------------------------------
// The in-basket category filter groups types into a handful of human categories. Each
// dropdown option carries an icon. Order here is the order the dropdown renders.

export interface NotificationCategory {
  value: string;
  label: string;
  icon: IconName<"regular">;
}

// Every category a notification type can map to (see TYPE_TO_CATEGORY). This is the full set, so a
// label lookup covers every type — the chart Overview "Updates" card reads it to title each row, and
// it isn't behind the in-app-notifications flag. Trimming it here would make every meds / imaging /
// forms / billing row on that card read "Other".
export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  { value: "labs", label: "Labs", icon: "flask-vial" },
  { value: "records", label: "Records", icon: "file-circle-plus" },
  { value: "calendar", label: "Calendar", icon: "calendar-xmark" },
  { value: "fax", label: "Fax", icon: "fax" },
  { value: "imaging", label: "Imaging", icon: "x-ray" },
  { value: "forms", label: "Questionnaires", icon: "clipboard-list" },
  { value: "billing", label: "Billing", icon: "money-check-alt" },
  { value: "meds", label: "Meds", icon: "prescription" },
  { value: "messages", label: "Messages", icon: "comments" },
];

// The subset the in-basket filter dropdown offers — only the categories whose notification types
// have a producer. Add a value here when its notification type ships; the full set above stays
// complete regardless.
const MVP_FILTER_CATEGORY_VALUES = ["labs", "records", "calendar", "fax", "forms", "messages"];
export const NOTIFICATION_FILTER_CATEGORIES: NotificationCategory[] =
  NOTIFICATION_CATEGORIES.filter((category) => MVP_FILTER_CATEGORY_VALUES.includes(category.value));

const TYPE_TO_CATEGORY: Record<string, string> = {
  [NotificationType.AppointmentBooked]: "calendar",
  [NotificationType.AppointmentCancelled]: "calendar",
  [NotificationType.AppointmentRescheduled]: "calendar",
  [NotificationType.LabsReceived]: "labs",
  [NotificationType.ImagingOrderStatus]: "imaging",
  [NotificationType.QuestionnaireCompleted]: "forms",
  [NotificationType.FaxReceived]: "fax",
  [NotificationType.LabUnpaid]: "billing",
  [NotificationType.RecordUploaded]: "records",
  [NotificationType.ErxStatus]: "meds",
  [NotificationType.PriorAuth]: "meds",
  [NotificationType.RxTransmissionIssue]: "meds",
  [NotificationType.MedsInCart]: "meds",
  [NotificationType.ThreadAssigned]: "messages",
  [NotificationType.ThreadRouted]: "messages",
};

/** The human category a notification type belongs to, for the in-basket filter. */
export function getNotificationCategory(type: string | undefined): string | undefined {
  return type ? TYPE_TO_CATEGORY[type] : undefined;
}

//--------------------------------------------------------------------------------------------------
//                                     RESOLVED CTA
//--------------------------------------------------------------------------------------------------

// The focused resource the in-basket flyout opens for a notification — rather than the whole
// patient-chart tab.
export type NotificationView =
  | { kind: "document"; id: string } // DocumentReference (uploaded record, fax, or lab report file)
  | { kind: "questionnaire"; id: string } // QuestionnaireResponse → completed form
  | { kind: "lab"; id: string } // DiagnosticReport → lab report PDF
  | { kind: "lab-order"; id: string } // ServiceRequest → its lab report, found when the flyout opens
  | { kind: "appointment"; id: string }; // Appointment → appointment detail

// The resolved CTA for a notification instance — label plus how it opens.
export interface NotificationCta {
  label: string;
  // Chart tab the CTA opens for the notification's patient (still used by the chart Overview card
  // to open a tab beside the current one).
  targetTab?: TabType;
  // Deep-link payload for the target tab (e.g. RecordsTab opens `documentId`/`labResultId`
  // full-screen). Absent when there's no real resource to open — the tab just lands on its list.
  data?: Record<string, unknown>;
  // A page the CTA navigates to instead of opening the flyout - the thread of a conversation
  // notification.
  href?: string;
  // The focused resource the in-basket flyout opens (PDF / lab / appointment). Absent when no real
  // resource backs the notification — the flyout shows a "no preview" message.
  view?: NotificationView;
  // True when the CTA should open the "match fax to patient" flow (unmatched fax).
  faxMatch?: boolean;
  // The fax's DocumentReference id (the notification focus), when `faxMatch` is set and a real fax
  // document exists. The match flow finds the Fax (Communication) from this document.
  faxDocumentId?: string;
}

// Splits a notification's focus reference ("DocumentReference/abc") into [type, id].
// A synthetic dev-seed id ("seed-…") is treated as "no real resource": the CTA still opens
// the right tab, but without a deep-link that would 404 the viewer.
function realFocus(notification: Notification): { type: string; id: string } | undefined {
  const focus = notification.focus;
  if (!focus) return undefined;
  const [type, id] = focus.split("/");
  if (!type || !id || id.startsWith("seed-")) return undefined;
  return { type, id };
}

/**
 * Resolves a notification's CTA — the label, the chart tab it opens, and (when a real resource
 * backs it) the deep-link data that opens the exact document. Fax is contextual: a fax matched
 * to a patient opens that patient's Records document; an unmatched fax opens the match flow.
 */
export function resolveNotificationCta(notification: Notification): NotificationCta {
  const display = getNotificationDisplay(notification.type);
  const focus = realFocus(notification);

  // Conversation: open the thread page the producer linked.
  if (isConversationNotificationType(notification.type)) {
    return { label: display.ctaLabel, href: notification.link };
  }

  // Fax: matched → open the fax document PDF; unmatched → match-to-patient flow.
  if (notification.type === NotificationType.FaxReceived) {
    if (notification.patientRef) {
      const isDoc = focus?.type === "DocumentReference";
      return {
        label: "Review",
        targetTab: TabType.Records,
        data: isDoc ? { documentId: focus?.id } : undefined,
        view: isDoc && focus ? { kind: "document", id: focus.id } : undefined,
      };
    }
    const faxDocumentId = focus?.type === "DocumentReference" ? focus.id : undefined;
    return { label: "Match", faxMatch: true, faxDocumentId };
  }

  // Questionnaire: open the completed response (flyout) or the Records questionnaire viewer (chart).
  if (notification.type === NotificationType.QuestionnaireCompleted) {
    const isResponse = focus?.type === "QuestionnaireResponse";
    return {
      label: display.ctaLabel,
      targetTab: display.targetTab,
      data: isResponse ? { questionnaireResponseId: focus?.id } : undefined,
      view: isResponse && focus ? { kind: "questionnaire", id: focus.id } : undefined,
    };
  }

  // Labs/records open their document PDF; appointments open the appointment detail — when a real
  // resource backs the notification. `data` stays for the chart Overview's tab-deep-link path.
  let data: Record<string, unknown> | undefined;
  let view: NotificationView | undefined;
  const isLabOrRecord =
    notification.type === NotificationType.LabsReceived ||
    notification.type === NotificationType.RecordUploaded;
  if (isLabOrRecord && focus?.type === "DocumentReference") {
    data = { documentId: focus.id };
    view = { kind: "document", id: focus.id };
  } else if (
    notification.type === NotificationType.LabsReceived &&
    focus?.type === "DiagnosticReport"
  ) {
    // Flyout shows the lab report PDF; the chart opens the Records lab viewer.
    data = { labResultId: focus.id };
    view = { kind: "lab", id: focus.id };
  } else if (
    notification.type === NotificationType.LabsReceived &&
    focus?.type === "ServiceRequest"
  ) {
    // The labs producer falls back to the order when no result existed yet at emit time. The
    // flyout looks the result up by order when it opens.
    view = { kind: "lab-order", id: focus.id };
  } else if (isAppointmentNotificationType(notification.type) && focus?.type === "Appointment") {
    view = { kind: "appointment", id: focus.id };
  }

  return { label: display.ctaLabel, targetTab: display.targetTab, data, view };
}

/** True for notification types about a patient conversation. Their CTA opens the thread. */
function isConversationNotificationType(type: string | undefined): boolean {
  return type === NotificationType.ThreadAssigned || type === NotificationType.ThreadRouted;
}

/** True for notification types whose flyout previews an appointment detail. */
export function isAppointmentNotificationType(type: string | undefined): boolean {
  return (
    type === NotificationType.AppointmentBooked ||
    type === NotificationType.AppointmentCancelled ||
    type === NotificationType.AppointmentRescheduled
  );
}

//--------------------------------------------------------------------------------------------------
//                                       CHART LINK
//--------------------------------------------------------------------------------------------------

// Chart query key that opens each record kind in the Records tab. A lab-order view has no result id
// to link, and an appointment has no Records viewer, so both open the chart without a record.
const RECORD_QUERY_KEY: Partial<Record<NotificationView["kind"], string>> = {
  document: PatientEhrQuery.documentId,
  questionnaire: PatientEhrQuery.formId,
  lab: PatientEhrQuery.labId,
};

/**
 * The patient chart URL for the flyout header. When the flyout shows a record, the URL deep-links
 * the Records tab to that same record.
 */
export function getNotificationChartHref({
  practiceSlug,
  patientId,
  view,
}: {
  practiceSlug: string;
  patientId: string;
  view?: NotificationView;
}): string {
  const recordKey = view ? RECORD_QUERY_KEY[view.kind] : undefined;
  const query =
    view?.id && recordKey
      ? { [PatientEhrQuery.tab]: TabType.Records, [recordKey]: view.id }
      : undefined;
  return buildPatientChartHref({ practiceSlug, patientId, query });
}

/**
 * The unread badge text for My Inbox — the nav icon, the inbox title, and the home card all show
 * it, so they always match. Undefined when nothing is unread. Capped so a large backlog doesn't
 * blow out the nav rail.
 */
export function formatUnreadBadge(unreadCount: number): string | undefined {
  if (unreadCount <= 0) return undefined;
  if (unreadCount > 99) return "99+";
  return unreadCount.toString();
}
